import React, { useRef } from 'react';
import { styled } from '@mui/material';

import { NoMilestones } from 'components/organisms/Milestones';
import {
    Button,
    Box,
    Checkbox,
    Dropdown,
    Icon,
    IconButton,
    ScrollableTable,
    ScrollableTableHead,
    ScrollableTableBody,
    ScrollableTableRow,
    ScrollableTableCell,
    SplitButton,
    Stack,
    TextInput,
    DatePicker,
} from 'shell/components';
import { getDueDate, getTemplateStatusWithIcon } from 'utils/helper';
import moment from 'moment';

const Tablet = ({
    filterDrawer,
    numFilters,
    setFilterDrawer,
    calendarOpen,
    setCalendarOpen,
    milestones,
    checked,
    onChangeCheck,
    onChangeCheckAll,
    getCompletion,
    onClickMilestone,
    onClickCreate,
    onClickPublishMulti,
    onClickDeleteMulti,
    onClickViewSubmissions,
    duplicateMilestone,
    deleteMilestone,
    handleMultiDuedate,
}) => {
    const anchorRef = useRef(null);
    const handleClickSplitButton = ({ key }) => {
        if (key === 'publish') onClickPublishMulti();
        if (key === 'delete') onClickDeleteMulti();
    };

    const handleSelect = ({ key }, milestoneId) => {
        if (key === 'edit') onClickMilestone(milestoneId);
        if (key === 'duplicate') duplicateMilestone(milestoneId);
        if (key === 'delete') deleteMilestone(milestoneId);
    };

    return (
        <CustomStack spacing={2}>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack direction={'row'} spacing={2}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setFilterDrawer((current) => ({ ...current, isOpen: true }))}
                    >
                        {`Filters ${numFilters ? `(${numFilters})` : ''}`}
                    </Button>
                    <CustomTextInput
                        disablePointerEventsOnIcon={!Boolean(filterDrawer.searchInput)}
                        endIcon={
                            filterDrawer.searchInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => setFilterDrawer((current) => ({ ...current, searchInput: '' }))}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={filterDrawer.searchInput}
                        onChange={(e) => setFilterDrawer((current) => ({ ...current, searchInput: e.target.value }))}
                    />
                </Stack>
                {checked.length ? (
                    <Stack direction={'row'} spacing={4}>
                        <CustomBox>
                            <Button ref={anchorRef} onClick={() => setCalendarOpen(true)} variant={'tertiary'}>
                                Change Due Date(s)
                            </Button>
                            <DatePicker
                                ref={anchorRef}
                                disabledDate={(date) => date.isSameOrBefore(moment().subtract(1, 'day'))}
                                calendarOnly={true}
                                calendarOpen={calendarOpen}
                                value={moment()}
                                onChange={(date) => handleMultiDuedate(date)}
                            />
                        </CustomBox>
                        <SplitButton
                            options={[
                                { key: 'publish', value: 'Publish' },
                                { key: 'delete', value: 'Delete' },
                            ]}
                            onClick={handleClickSplitButton}
                        />
                    </Stack>
                ) : (
                    <Button
                        data-cy={'create-milestone-button'}
                        startIcon={<Icon kind={'add'} />}
                        onClick={onClickCreate}
                    >
                        Create Milestone
                    </Button>
                )}
            </Stack>
            {!milestones.length ? (
                <NoMilestones />
            ) : (
                <CustomScrollableTable>
                    <ScrollableTableHead>
                        <ScrollableTableRow>
                            <ScrollableTableCell isFixed>
                                <Checkbox
                                    checked={milestones.every(({ id }) => checked.includes(id))}
                                    onChange={(_, checked) => onChangeCheckAll(checked)}
                                />
                            </ScrollableTableCell>
                            <ScrollableTableCell isFixed>Name</ScrollableTableCell>
                            <ScrollableTableCell>Status</ScrollableTableCell>
                            <ScrollableTableCell>Due Date</ScrollableTableCell>
                            <ScrollableTableCell>Milestone Submissions</ScrollableTableCell>
                            <ScrollableTableCell isFixed></ScrollableTableCell>
                        </ScrollableTableRow>
                    </ScrollableTableHead>
                    <ScrollableTableBody>
                        {milestones.map((milestone) => (
                            <ScrollableTableRow key={milestone.id}>
                                <ScrollableTableCell isFixed>
                                    <Checkbox
                                        checked={checked.includes(milestone.id)}
                                        onChange={() => onChangeCheck(milestone.id)}
                                    />
                                </ScrollableTableCell>
                                <ScrollableTableCell isFixed>
                                    <Button
                                        sx={{ textAlign: 'left' }}
                                        color={'neutral'}
                                        variant={'borderless'}
                                        onClick={() => onClickMilestone(milestone.id)}
                                    >
                                        {milestone.title}
                                    </Button>
                                </ScrollableTableCell>
                                <ScrollableTableCell>{getTemplateStatusWithIcon(milestone)}</ScrollableTableCell>
                                <ScrollableTableCell>{getDueDate(milestone)}</ScrollableTableCell>
                                <ScrollableTableCell>
                                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                        {getCompletion(milestone)}
                                        <CustomButton
                                            disabled={getCompletion(milestone) === '--'}
                                            variant={'borderless'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClickViewSubmissions(milestone.id);
                                            }}
                                        >
                                            View Submissions
                                        </CustomButton>
                                    </Stack>
                                </ScrollableTableCell>
                                <ScrollableTableCell isFixed>
                                    <Dropdown
                                        id={`dropdown-${milestone.id}`}
                                        options={[
                                            { key: 'edit', value: 'Edit' },
                                            { key: 'duplicate', value: 'Duplicate' },
                                            { key: 'delete', value: 'Delete' },
                                        ]}
                                        rootComponent={
                                            <IconButton kind={'moreHorizontal'} size={'small'} variant={'square'} />
                                        }
                                        onSelect={(option) => handleSelect(option, milestone.id)}
                                    />
                                </ScrollableTableCell>
                            </ScrollableTableRow>
                        ))}
                    </ScrollableTableBody>
                </CustomScrollableTable>
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        paddingTop: theme.spacing(4),
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const CustomScrollableTable = styled(ScrollableTable)(({ theme }) => {
    return {
        width: '100%',
        minHeight: 'calc(100vh - 206px)',
        '& .MuiTableCell-root': {
            '&:not(:last-child)': {
                borderRight: `1px solid ${theme.palette.ensightful.neutral[300]}`,
            },
            '&:nth-child(1)': {
                left: 0,
                width: '73px',
            },
            '&:nth-child(2)': {
                left: '73px',
                width: '168px',
                minWidth: '168px',
                maxWidth: '168px',
            },
            '&:last-child': {
                right: 0,
                borderLeft: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                width: '56px',
            },
            '&:nth-last-child(2)': {
                borderRight: 'none',
            },
        },
    };
});

const CustomButton = styled(Button)(({ theme }) => {
    return {
        margin: theme.spacing(0, 1),
    };
});

const CustomBox = styled(Box)(() => {
    return {
        position: 'relative',
    };
});

export default Tablet;
